import type { DifficultyConfig, DifficultyTier } from '../config/difficulty.config';
import { clamp01 } from '../utils/math';

/** Maps run distance to speed and difficulty tier. Pure function of distance – deterministic. */
export class DifficultyManager {
  private readonly tiers: readonly DifficultyTier[];

  constructor(private readonly config: DifficultyConfig) {
    if (config.tiers.length === 0) throw new Error('DifficultyConfig needs at least one tier');
    this.tiers = [...config.tiers].sort((a, b) => a.fromDistance - b.fromDistance);
  }

  speedAt(distance: number): number {
    const { start, max, rampDistance, rampExponent, earlyBoost } = this.config.speed;
    const t = clamp01(distance / rampDistance) ** rampExponent;
    const fade = clamp01((distance - earlyBoost.until) / Math.max(1, earlyBoost.fadeOutAt - earlyBoost.until));
    return (start + (max - start) * t) * (earlyBoost.factor + (1 - earlyBoost.factor) * fade);
  }

  /** 0 at start speed, 1 at max speed – drives speed FOV etc. A run that starts at top speed (?speed= testing aid) is 1. */
  normalizedSpeed(speed: number): number {
    const { start, max } = this.config.speed;
    const span = max - start;
    return span > 0 ? clamp01((speed - start) / span) : 1;
  }

  tierAt(distance: number): DifficultyTier {
    return this.tiers[this.levelAt(distance) - 1] as DifficultyTier;
  }

  /** Level shown to the player: 1 for the first tier, +1 per tier reached. */
  levelAt(distance: number): number {
    let level = 1;
    for (let i = 1; i < this.tiers.length; i++) {
      if (distance >= (this.tiers[i] as DifficultyTier).fromDistance) level = i + 1;
    }
    return level;
  }

  get reactionTime(): number {
    return this.config.reactionTime;
  }
}
